import { matchRoutes } from "react-router-dom";

const BASE_API_URL = `${process.env.REACT_APP_API_URL}`;
const router = "";

export const GHOST_ADMIN_ORIGIN =
  process.env.REACT_APP_GHOST_ADMIN_ORIGIN || "https://spotlight.stanzacal.com";

// Read feature configuration here so UI behavior has one mockable config boundary.
export const getCalendarLimitInterstitialFatigueDays = () =>
  process.env.REACT_APP_CALENDAR_LIMIT_INTERSTITIAL_FATIGUE_DAYS;

export const getCalendarReviewInterstitialFatigueDays = () =>
  process.env.REACT_APP_CALENDAR_REVIEW_INTERSTITIAL_FATIGUE_DAYS;

export const getGoogleConnectInactiveInterstitialFatigueDays = () =>
  process.env.REACT_APP_GOOGLE_CONNECT_INACTIVE_INTERSTITIAL_FATIGUE_DAYS;

export const isCalendarPersonalizationEnabled = () =>
  process.env.REACT_APP_CALENDAR_PERSONALIZATION === "true";

/** `mock` serves the in-browser mock; any other value, empty or unrecognised, calls the server. */
export const getCalendarPersonalizationApiMode = () =>
  process.env.REACT_APP_CALENDAR_PERSONALIZATION_API === "mock" ? "mock" : "live";

export const HomeRoutes = {
  apiRoutes: Object.freeze({
    GET_TRENDING_CALENDARS: `${router}/calendar/trending`, // Get all calendars of the DB
    GET_CALENDARS_SECTIONS: `${router}/discovery/trendingsections`, // Get all sections of the home page
    GET_CALENDARS_BY_TAGS: `${router}/discovery/calendarsbytags`, // Search calendars by tags
    GET_LEADERBOARD_BY_TAGS: `${router}/discovery/leaderboardbytags`, // Search leaderboards by tags
    GET_SECTIONS: `${router}/sections`, // Paginated rail feed for home and category pages
    GET_SECTION_BY_SLUG: `${router}/sections/:slug`, // One section's metadata
    GET_SECTION_CALENDARS: `${router}/sections/:slug/calendars`, // Paginated calendars from one section snapshot
  }),
  pageRoutes: Object.freeze({
    HOME: `/`,
    TAG: "/tag/:tag",
    MOVIE_GENRE: "/movies/:genre", // Film-only genre page — ["movies", genre] tag intersection
    EXPLORE_CATEGORY: "/explore/:category", // Category-scoped rail feed (sports, tv, music, general)
    SECTION_SEE_ALL: "/sections/:slug", // Leaderboard + A-Z view of a single section
    PERSONALIZE: "/personalize",
    PERSONALIZATION: "/personalization", // Stanza+ calendar-personalization story + early-access waitlist
    VISION: "/vision",
  }),
};

export const ElectionsRoutes = {
  apiRoutes: Object.freeze({
    GET_CONFIG: `${router}/elections/config`, // The sentinel calendarId, for routing into the concierge (public)
    GET_STATUS: `${router}/elections/status`, // Whether the user is subscribed + their saved address/state/calendarId
    GET_STATE_PREVIEW: `${router}/elections/state-preview/:slug`, // Upcoming key dates for a state's election calendar (public)

    PREVIEW_ADDRESS: `${router}/elections/preview`, // Normalize an address and resolve its state (422 if not found)
    SUBSCRIBE: `${router}/elections/subscribe`, // Save the address and subscribe to the elections calendar
    UPDATE_ADDRESS: `${router}/elections/address`, // Edit the saved address in place without re-subscribing
  }),
  pageRoutes: Object.freeze({
    US_ELECTIONS: `/us-elections`,
  }),
};

export const PressRoutes = {
  apiRoutes: Object.freeze({}),
  pageRoutes: Object.freeze({
    US_ELECTIONS: `/press/us-elections`,
  }),
};

export const AuthRoutes = {
  apiRoutes: Object.freeze({
    GET_AUTH: `${router}/auth`, // Get user's auth status
    POST_SIGNOUT: `${router}/auth/signout`, // Sign out user
    POST_SIGNIN: `${router}/auth/signin`, // Sign in user
    POST_EMAIL_SIGNIN: `${router}/auth/email-signin`, // Redeem an email magic-link token
    UPDATE: `${router}/auth/update`, // Update user's profile
    CONNECT_GOOGLE_CALENDAR: `${router}/auth/googlecalendar`, // Connect a user's Google Calendar
  }),
  pageRoutes: Object.freeze({
    SIGNIN: `/signin`, // Sign in user
    GOOGLE_CALENDAR_CALLBACK: `/auth/googlecalendar`, // Callback for Google Calendar
    EMAIL_SUBSCRIBE: `/email-subscribe`, // Magic-link entry from marketing emails (?token=&next= query params)
  }),
};

export const CalendarRoutes = {
  apiRoutes: Object.freeze({
    CREATE_CALENDAR: `${router}/calendar/create`, // Import a new calendar
    UPDATE_CALENDAR: `${router}/calendar/:handle/:calendarId`, // Update calendar info
    DELETE_CALENDAR: `${router}/calendar/delete/:calendarId`, // Delete a calendar
    GET_CALENDARS: `${router}/calendar/:handle`, // Get all calendars associated with a handle
    GET_CALENDAR: `${router}/calendar/:handle/:calendarId`, // Get calendar info
    GET_CALENDAR_EVENTS: `${router}/calendar/contents/:link`, // get the webcal contents (events) associated with calendar (link is the webcal link)
    GET_PROFILE_CALENDAR_EVENTS: `${router}/calendar/profile/contents/:link`, // Same as above but without cache
    GET_CALENDAR_SAMPLE: `${router}/calendar/sample/:handle/:calendarId`, // Get a sample of a calendar
    GET_CALENDAR_REVIEWS: `${router}/calendars/:calendarId/reviews`, // Paginated list of reviews for a calendar
    UPSERT_CALENDAR_REVIEW: `${router}/calendars/:calendarId/reviews`, // Create or update the viewer's review on a calendar
    GET_REVIEW_PROMPT: `${router}/calendars/review-prompt`, // The viewer's next unreviewed subscribed calendar (review interstitial)
    GET_CALENDAR_PERSONALIZATION: `${router}/calendar/personalization/:calendarId`, // What a calendar's events support for personalization + preview events
  }),
  pageRoutes: Object.freeze({
    ALL_EVENTS: `/:handle`, // View upcoming and past events for any calendar on Stanza
    CALENDAR_EVENTS: `/:handle/:calendarId`, // View upcoming and past events for a specific calendar
    CALENDAR_SETTINGS: `/:handle/:calendarId/settings`, // View settings for a specific calendar or group
    IMPORT: `/import`, // Import a new calendar
    LEGACY_TIMELINE: `/timeline/:handle`, // Legacy timeline pages
    LEGACY_SUBSCRIPTION: `/timeline/:handle/subscription`, // Legacy subscription pages
    LEGACY_SCHEDULES: `/schedules/:handle`, // Legacy schedules pages
    PREVIEW_CALENDAR: `/preview/:handle`, // Preview a creator's calendar
    PREVIEW_CALENDAR_EVENTS: `/preview/:handle/:calendarId`, // Preview fan's view of a creator's calendar
    EASY_SUBSCRIBE: `/subscribe/:handle`, // Easy-subscribe landing page (Apple/Google/Outlook buttons)
  }),
};

export const UserRoutes = {
  apiRoutes: Object.freeze({
    VALIDATE_SUBSCRIPTION: `${router}/user/validate-subscription`, // Validate if a user can subscribe to a calendar
    ADD_CALENDAR: `${router}/user/add`, // Add a calendar to a user's list of added calendars
    REMOVE_CALENDAR: `${router}/user/remove`, // Remove a calendar from a user's list of added calendars
    VERIFY_CALENDAR: `${router}/user/verify`, // Verify a calendar subscription
    CREATE_GROUP: `${router}/user/group/create`, // Create a new group
    UPDATE_GROUP: `${router}/user/group/update`, // Update a group's metadata that a user has created
    DELETE_GROUP: `${router}/user/group/delete`, // Delete a group that a user has created
    ADD_CALENDARS_TO_GROUP: `${router}/user/group/addcalendars`, // Add calendars to a group
    REMOVE_CALENDAR_FROM_GROUP: `${router}/user/group/removecalendar`, // Remove calendars from a group
    ADD_GROUP: `${router}/user/group/add`, // Add a group to a user's list of added groups
    REMOVE_GROUP: `${router}/user/group/remove`, // Remove a group from a user's list of added groups
    GET_USER_CALENDARS: `${router}/user/calendars`, // Get user's added & created calendars
    GET_USER_EVENTS: `${router}/user/events`, // Get user's upcoming events
    GET_MEMBERSHIP: `${router}/user/membership`, // Get the user's Stanza+ member number
    CHOOSE_FREE_PLAN: `${router}/user/choosefreeplan`, // Choose the free plan for a user
    ADD_TAGS: `${router}/user/tags/add`, // Add tags to a user's profile
    REMOVE_TAGS: `${router}/user/tags/remove`, // Remove tags from a user's profile
    UPDATE_TAGS: `${router}/user/tags/update`, // Update a user's tags
    MIGRATE_RELAY_EMAIL: `${router}/user/migrate-relay-email`, // Confirm Apple-relay email migration via relay token + fresh Apple idToken
    UPDATE_NICKNAME: `${router}/user/nickname`, // Update the user's review nickname (moderated)
    EMAIL_PREFERENCES: `${router}/user/email-preferences`, // Read/update email-preference categories (session | ?token= | ?uid=)
    CREATOR_SHARING: `${router}/user/creator-sharing`, // Read/update per-creator email-sharing (session | ?token= | ?uid=)
    CALENDAR_PREFERENCES: `${router}/user/calendar-preferences/:calendarId`, // Read/replace one calendar's event personalization (null inherits)
    USER_PREFERENCES: `${router}/user/preferences`, // Read/patch the user's preferences (calendarDefaults)
  }),
  pageRoutes: Object.freeze({
    UPDATE_RELAY_EMAIL: `/user/update-relay-email`, // Landing page reached from the ICS magic-link to confirm Apple-relay email migration
    EMAIL_PREFERENCES: `/email/settings`, // Email preference center (public; identity via session/token/uid)
    EVENT_DEFAULTS: `/settings/event-defaults`, // Default event personalization applied to every calendar
  }),
};

export const EmailRoutes = {
  apiRoutes: Object.freeze({
    TRIGGER_JUST_SYNCED_EMAIL: `${router}/email/just-synced`, // Trigger "just synced" email
    TRIGGER_ABANDONED_CHECKOUT: `${router}/email/abandoned-checkout`, // Record checkout landing for abandonment email
  }),
  pageRoutes: Object.freeze({}),
};

export const ImageRoutes = {
  apiRoutes: Object.freeze({
    UPDATE_PROFILE_IMAGE: `${router}/image/updateprofile`, // Update user's profile image
    UPDATE_IMAGE_URL: `${router}/image/:calendarId/:eventId`, // Update the image url for a specific event
  }),
};

export const LibraryRoutes = {
  apiRoutes: Object.freeze({
    GET_GOOGLE_CALENDAR_STATUS: `${router}/googlecalendar/status`,
    FORCE_REFRESH_GOOGLE_CALENDAR: `${router}/googlecalendar/refresh/:type/:calendarId/:handle`,
    DISCONNECT_GOOGLE_CALENDAR: `${router}/googlecalendar/disconnect`,
  }),
  pageRoutes: Object.freeze({
    LIBRARY: `/library`, // View all added or imported calendars, and created groups by the user
  }),
};

export const UpgradeRoutes = {
  apiRoutes: Object.freeze({
    STRIPE_CHECKOUT: `${router}/stripe/checkout`, // Create a new Stripe checkout session
    STRIPE_NATIVE_CHECKOUT: `${router}/stripe/nativecheckout`, // Create a new Stripe native checkout session
    STRIPE_CUSTOMER_PORTAL: `${router}/stripe/customerportal`, // Create a new Stripe customer portal session
    STRIPE_PRICE_DETAILS: `${router}/stripe/pricedetails`,
  }),
  pageRoutes: Object.freeze({
    UPGRADE_PLUS: `/upgrade/plus`,
    PAYMENT_STANZA_PLUS_CONFIRMATION: `/upgrade/confirmation`,
    UPGRADE_TRIGGER: `/upgrade/trigger`,
    MEMBERSHIP: `/membership`,
    MANAGE_SUBSCRIPTION: `/manage-subscription`,
  }),
};

export const CreatorRoutes = {
  apiRoutes: Object.freeze({
    CLAIM: `${router}/creators/claim`, // Silently claim the signed-in creator's automated calendars (wizard Step 1 mount)
    TEAM_CLAIM: `${router}/creators/team-claim`, // Public: express interest in taking over a team's calendar (emails riley@ + the user)
    ANALYTICS: `${router}/creators/analytics`, // Precomputed analytics for the signed-in creator's calendars
    SUBSCRIBERS: `${router}/creators/subscribers`, // Subscriber list for the signed-in creator (emails masked until the claim is complete)
    SUBSCRIBERS_EXPORT: `${router}/creators/subscribers/export`, // Streamed CSV of the full subscriber list (collecting creators only)
    EMAIL_COLLECTION: `${router}/creators/email-collection`, // Turn on email collection for the signed-in creator
    TRACKED_URLS: `${router}/creators/tracked-urls`, // List (GET) or add (POST) URLs to verify the Stanza link on
    DELETE_TRACKED_URL: `${router}/creators/tracked-urls/:id`, // Remove one tracked URL by its id
  }),
  pageRoutes: Object.freeze({
    ONBOARDING_ADD_LINK: `/creator-onboarding/add-link`, // Wizard Step 1 — add the Stanza link to site + socials
    ONBOARDING_VERIFY: `/creator-onboarding/verify`, // Wizard Step 2 — verify each tracked URL is live
    CREATOR_GET_STARTED: `/creators/get-started`, // Post-signup onboarding walkthrough (page anatomy + customization)
    CREATOR_ANALYTICS: `/creators/analytics`, // Creator analytics explainer
    CREATOR_INSPIRATION: `/creators/inspiration`, // Social + website examples for promoting a calendar
    CREATOR_CLAIM_EXPLAINER: `/creators/claim`, // What claiming an automated calendar means
    CASE_STUDY: `/creators/case-studies/:league`, // Per-league adoption case study (reusable template)
    PROPOSAL_SPORTS: `/creators/proposals/sports/:handle`, // Sales proposal personalized to a prospect's live calendar (unindexed)
    PROPOSAL_NCAA: `/creators/proposals/sports/ncaa/:handle`, // NCAA flavor of the sports proposal: adds the NIL premium section
    PROPOSAL_PLATFORM: `/creators/proposals/platforms/:slug`, // Platform pitch (e.g. sidearm) over a live example school; carries the build-vs-integrate calculator
    PROPOSAL_PREMIUM: `/creators/proposals/premium/:slug`, // Premium-calendar pitch for influencers; rendered from a bucket config, no deploy per prospect (unindexed)
  }),
};

export const HelpRoutes = {
  pageRoutes: Object.freeze({
    HELP_IMPORT: `/help/import`,
    CONCIERGE: `/concierge/:handle/:calendarId`,
    SUBSCRIBING_GOOGLE_CALENDAR: `/subscribing/google-calendar/:handle/:calendarId`,
    SUBSCRIBING_CALENDAR: `/subscribing/calendar/:handle/:calendarId`,
    SUBSCRIBING_GOOGLE_CALENDAR_STEPS: `/subscribing/google-calendar/steps/:handle/:calendarId`,
    SUBSCRIBING_GOOGLE_CALENDAR_STEPS_STANDALONE: `/subscribing/google-calendar/steps`,
    SUBSCRIBING_CALENDAR_STEPS: `/subscribing/calendar/steps/:handle/:calendarId`,
    SUBSCRIBING_CALENDAR_STEPS_STANDALONE: `/subscribing/calendar/steps`,
    SUBSCRIBING_CALENDAR_EXPERIENCE: `/subscribing/calendar/experience/:handle/:calendarId`,
    SUBSCRIBING_CALENDAR_VERIFY: `/subscribing/calendar/verify/:handle/:calendarId`,
    SUBSCRIBING_CALENDAR_SUCCESS: `/subscribing/calendar/success/:handle/:calendarId`,
    SUBSCRIBING_CALENDAR_VERIFY_SCREEN: `/subscribing/calendar/verify-screen/:handle/:calendarId`,
    CONNECT_GOOGLE_CALENDAR: `/connect/google-calendar`,
    GOOGLE_CONNECT_OFF: `/connect/google-calendar/off`,
    GOOGLE_CALENDAR_CONNECTED: "/subscribing/google-calendar/connected",
    CALENDAR_CONNECTED: "/subscribing/calendar/connected/:handle/:calendarId",
    FREE_TRIAL_STEPS: "/free-trial/steps",
    CHOOSE_FREE_PLAN: "/choose-free-plan",
  }),
};

export const ChatRoutes = {
  apiRoutes: Object.freeze({
    SEARCH: `${router}/chat/search`, // Search for calendars and groups
    DISCOVER: `${router}/chat/discover`, // Search for calendars and groups
    SUPPORT: `${router}/chat/support`, // Search for calendars and groups
    RELATED_CALENDARS: `${router}/chat/relatedCalendars`, // Get related calendars
  }),
  pageRoutes: Object.freeze({
    SEARCH: `/search`, // Search for calendars and groups
    SEARCH_TAB: `/search/:activeTab`, // Search for calendars and groups
  }),
};

export const EmbeddedRoutes = {
  pageRoutes: Object.freeze({
    EMBEDDED_TIMELINE_EVENTS: `/embedded/:handle/:calendarId`, // Timeline Events of specific calendar for embedding
  }),
};

export const PrivacyRoutes = {
  pageRoutes: Object.freeze({
    PRIVACY: `/privacy`, // Privacy policy
  }),
};

export const TermsRoutes = {
  pageRoutes: Object.freeze({
    TERMS: `/terms`, // Terms of service
  }),
};

export const EventRoutes = {
  apiRoutes: Object.freeze({
    CREATE_EVENT: `${router}/event/create`,
    UPDATE_EVENT: `${router}/event/update/:calendarId/:eventId`,
    DELETE_EVENT: `${router}/event/delete/:calendarId/:eventId`,
    GET_PUBLIC_EVENT: `${router}/event/:eventId`,
  }),
  pageRoutes: Object.freeze({
    EVENT_DETAIL: "/events/:eventId",
  }),
};

export const AnalyticsRoutes = {
  apiRoutes: Object.freeze({
    CRITICAL_EVENT: `${router}/analytics/events`,
    FUNNEL_MONTHLY: `${router}/funnel/monthly`,
    FUNNEL_DIMENSIONS: `${router}/funnel/dimensions`,
    FUNNEL_PROJECTION: `${router}/funnel/projection`,
    FUNNEL_REFRESH: `${router}/funnel/refresh`,
    FUNNEL_LTV: `${router}/funnel/ltv`,
    FUNNEL_USERBASE: `${router}/funnel/userbase`,
    FUNNEL_CHURN: `${router}/funnel/churn`,
    FUNNEL_CHURN_SIMULATE: `${router}/funnel/churn/simulate`,
    FUNNEL_EMAIL: `${router}/funnel/email`,
    PERFORMANCE_WEB_VITALS: `${router}/admin/analytics/performance/web-vitals`,
    PERFORMANCE_WEB_VITALS_ROUTE_RANKING: `${router}/admin/analytics/performance/web-vitals/route-ranking`,
    PERFORMANCE_BACKEND_ROUTES: `${router}/admin/analytics/performance/backend-routes`,
    PERFORMANCE_DIMENSIONS: `${router}/admin/analytics/performance/dimensions`,
  }),
  pageRoutes: Object.freeze({
    FUNNEL_DASHBOARD: `/analytics/funnel`,
    PERFORMANCE_DASHBOARD: `/analytics/performance`,
  }),
};

export const FeedbackRoutes = {
  apiRoutes: Object.freeze({
    POST_SUBMIT: `${router}/feedback`, // Submit user feedback
  }),
};

export const NcaaLinkRoutes = {
  apiRoutes: Object.freeze({
    LIST: `${router}/admin/ncaa-links`,
    RECENT: `${router}/admin/ncaa-links/recent`,
    REPLACE: `${router}/admin/ncaa-links/:calendarId/replace`,
    RECHECK: `${router}/admin/ncaa-links/:calendarId/recheck`,
  }),
  pageRoutes: Object.freeze({
    NCAA_DASHBOARD: `/admin/ncaa-links`,
  }),
};

export const PrintableScheduleRoutes = {
  apiRoutes: Object.freeze({
    UPLOAD: `${router}/calendar/:calendarId/printableschedule`, // Upload or replace a calendar's printable schedule PDF
    DELETE: `${router}/calendar/:calendarId/printableschedule`, // Remove a calendar's printable schedule PDF
  }),
  pageRoutes: Object.freeze({
    PRINTABLE_SCHEDULE_DASHBOARD: `/admin/printable-schedules`,
  }),
};

export const CreatorContactRoutes = {
  apiRoutes: Object.freeze({
    LIST: `${router}/admin/creator-contacts`,
    RECENT: `${router}/admin/creator-contacts/recent`,
    SUBMIT: `${router}/admin/creator-contacts/:handle/contact`,
  }),
  pageRoutes: Object.freeze({
    CREATOR_CONTACT_DASHBOARD: `/admin/creator-contacts`,
  }),
};

export const CreatorAcquisitionRoutes = {
  apiRoutes: Object.freeze({
    BOARD: `${router}/admin/creator-acquisition/board`,
    COMMS: `${router}/admin/creator-acquisition/comms`,
    DRAFTS: `${router}/admin/creator-acquisition/drafts`,
  }),
  pageRoutes: Object.freeze({
    CREATOR_ACQUISITION_DASHBOARD: `/admin/creator-acquisition`,
  }),
};

export const PerformanceRoutes = {
  apiRoutes: Object.freeze({
    DASHBOARD: `${router}/admin/performance/dashboard`,
  }),
  pageRoutes: Object.freeze({
    PERFORMANCE_DASHBOARD: `/admin/performance`,
  }),
};

export const SeoRoutes = {
  apiRoutes: Object.freeze({
    DASHBOARD: `${router}/admin/seo/dashboard`,
  }),
  pageRoutes: Object.freeze({
    SEO_DASHBOARD: `/admin/seo-pages`,
    PRODUCT_ADMIN: `/admin/product-pages`,
  }),
};

export const CalendarTriageRoutes = {
  apiRoutes: Object.freeze({
    LIST: `${router}/admin/calendar-triage`,
    OPPORTUNITIES: `${router}/admin/calendar-triage/opportunities`,
  }),
  pageRoutes: Object.freeze({
    CALENDAR_TRIAGE_DASHBOARD: `/admin/calendar-triage`,
  }),
};

export const SectionAdminRoutes = {
  apiRoutes: Object.freeze({
    LIST: `${router}/admin/sections`,
    GET: `${router}/admin/sections/:id`,
    UPDATE: `${router}/admin/sections/:id`,
    UPDATE_MEMBERS: `${router}/admin/sections/:id/members`,
  }),
  pageRoutes: Object.freeze({
    SECTIONS_DASHBOARD: `/admin/sections`,
  }),
};

export const GhostCampaignRoutes = {
  apiRoutes: Object.freeze({
    ESTIMATE: `${router}/ghost-campaigns/estimate`,
    LIST: `${router}/ghost-campaigns`,
    CREATE: `${router}/ghost-campaigns`,
    GET_ONE: `${router}/ghost-campaigns/:id`,
    UPDATE: `${router}/ghost-campaigns/:id`,
    PERFORMANCE: `${router}/ghost-campaigns/:id/performance`,
    CANONICAL_STATUS: `${router}/ghost-campaigns/:id/canonical-status`,
    RATINGS: `${router}/ghost-campaigns/ratings`,
    PHASE: `${router}/ghost-campaigns/:id/phase`,
    APPROVE: `${router}/ghost-campaigns/:id/approve`,
    DUPLICATE: `${router}/ghost-campaigns/:id/duplicate`,
    ARCHIVE: `${router}/ghost-campaigns/:id/archive`,
    REJECT: `${router}/ghost-campaigns/:id/reject`,
    RESTORE: `${router}/ghost-campaigns/:id/restore`,
    UPDATE_DRAFT: `${router}/ghost-campaigns/:id/update-draft`,
    DELETE_CAMPAIGN: `${router}/ghost-campaigns/:id`,
  }),
  pageRoutes: Object.freeze({
    CAMPAIGN_DASHBOARD: `/admin/campaigns`,
  }),
};

const routesEndpoints = {
  home: HomeRoutes,
  elections: ElectionsRoutes,
  press: PressRoutes,
  auth: AuthRoutes,
  calendar: CalendarRoutes,
  email: EmailRoutes,
  image: ImageRoutes,
  library: LibraryRoutes,
  upgrade: UpgradeRoutes,
  help: HelpRoutes,
  chat: ChatRoutes,
  embedded: EmbeddedRoutes,
  privacy: PrivacyRoutes,
  terms: TermsRoutes,
  event: EventRoutes,
  user: UserRoutes,
  analytics: AnalyticsRoutes,
  feedback: FeedbackRoutes,
  creator: CreatorRoutes,
  ncaaLinks: NcaaLinkRoutes,
  printableSchedules: PrintableScheduleRoutes,
  performance: PerformanceRoutes,
  seo: SeoRoutes,
  calendarTriage: CalendarTriageRoutes,
  creatorContacts: CreatorContactRoutes,
  creatorAcquisition: CreatorAcquisitionRoutes,
  sectionsAdmin: SectionAdminRoutes,
  ghostCampaigns: GhostCampaignRoutes,
};

const webVitalsExtraPageRoutes = [
  "/creators",
  "/admin",
  "/button/embed",
  "/button/launch",
  "/march-madness",
  "/event/:id",
];

const webVitalsPageRoutes = [
  ...Object.values(routesEndpoints).flatMap((routes) =>
    Object.values(routes.pageRoutes || {}),
  ),
  ...webVitalsExtraPageRoutes,
].map((path) => ({ path }));

// Resolve a pathname to a route template so performance telemetry never sends
// dynamic IDs, handles, or query parameters to analytics.
export const getWebVitalsRoute = (pathname) => {
  const matches = matchRoutes(webVitalsPageRoutes, pathname);
  return matches?.[matches.length - 1]?.route.path || "other";
};

// Generate a url with params object
export const generateParamUrl = (endpoint, params = {}) => {
  const url = Object.keys(params).reduce((prev, current) => {
    return prev.replace(new RegExp(`:${current}[?]?`, "g"), params[current]);
  }, endpoint);
  return url || endpoint;
};

// Get a page route
export const getPageRoute = (module, endpoint, params, queryParams = {}) => {
  const endpointUrl = routesEndpoints?.[module]?.pageRoutes?.[endpoint];
  let stringfiedParams = new URLSearchParams(queryParams).toString();
  if (stringfiedParams) stringfiedParams = `?${stringfiedParams}`;
  return `${generateParamUrl(endpointUrl, params)}${stringfiedParams}`;
};

// Get an api route
export const getApiRoute = (module, endpoint, params, queryParams = {}) => {
  const endpointUrl = routesEndpoints?.[module]?.apiRoutes?.[endpoint];
  let stringfiedParams = new URLSearchParams(queryParams).toString();
  if (stringfiedParams) stringfiedParams = `?${stringfiedParams}`;
  return `${BASE_API_URL}${generateParamUrl(
    endpointUrl,
    params,
  )}${stringfiedParams}`;
};
